/**
 * API 工具函数
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const LEADERBOARD_TAG = import.meta.env.VITE_LEADERBOARD_TAG || 'nba';

import { withErrorHandling, getErrorType, getUserFriendlyMessage, logError } from './errorHandler';

export function getStoredLang() {
  const raw = localStorage.getItem('lang') || 'zh';
  return raw.toLowerCase().startsWith('en') ? 'en' : 'zh';
}

function withLang(urlString) {
  const url = new URL(urlString);
  url.searchParams.set('lang', getStoredLang());
  return url.toString();
}

/**
 * 刷新访问令牌
 * @returns {Promise<boolean>} 刷新成功返回 true，失败返回 false
 */
export async function refreshAccessToken() {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    const now = Math.floor(Date.now() / 1000);
    const refreshExpireAt = localStorage.getItem('refresh_token_expire');

    // 检查refresh token是否存在
    if (!refreshToken) {
      console.log('Refresh token 不存在');
      return false;
    }

    // 检查refresh token是否已过期
    if (refreshExpireAt && parseInt(refreshExpireAt) <= now) {
      console.log('Refresh token 已过期');
      return false;
    }

    const response = await fetch(withLang(`${API_BASE_URL}/api/v1/user/refresh-token`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `refresh_token=${encodeURIComponent(refreshToken)}`,
    });

    const data = await response.json();

    if (data.code === 0 && data.data) {
      // 保存新的 token 对
      localStorage.setItem('access_token', data.data.access_token.token);
      localStorage.setItem('access_token_expire', data.data.access_token.expire_at);
      localStorage.setItem('refresh_token', data.data.refresh_token.token);
      localStorage.setItem('refresh_token_expire', data.data.refresh_token.expire_at);

      console.log('Token 刷新成功');
      return true;
    } else {
      console.error('Token 刷新失败:', data.msg);
      return false;
    }
  } catch (error) {
    console.error('Token 刷新失败:', error);
    return false;
  }
}

/**
 * 判断访问令牌是否即将过期（5分钟内）
 * @returns {boolean} 是否即将过期
 */
export function isAccessTokenExpiringSoon() {
  const expireAt = localStorage.getItem('access_token_expire');
  if (!expireAt) return true;

  const now = Math.floor(Date.now() / 1000);
  const threshold = 300; // 5分钟阈值
  return parseInt(expireAt) - now <= threshold;
}

// 请求队列管理，避免并发请求导致多次刷新
// 全局刷新 Promise，避免并发重复刷新
let refreshPromise = null;

/**
 * API 请求封装
 * @param {string} endpoint - API 端点
 * @param {object} options - fetch 选项
 * @returns {Promise<object>} API 响应数据
 */
export async function apiRequest(endpoint, options = {}) {
  const url = withLang(`${API_BASE_URL}${endpoint}`);

  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  // 如果有 token，添加到请求头
  const token = localStorage.getItem('access_token');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  const handleMembershipError = (response, data) => {
    if (response.status !== 403 && data?.code !== 40007) return null;
    const err = new Error(data?.msg || '会员权限不足，请升级会员');
    err.isMembershipRequired = true;
    err.currentTier = data?.data?.current_tier;
    err.payload = data;
    try {
      window.dispatchEvent(
        new CustomEvent('membership-required', {
          detail: { currentTier: err.currentTier }
        })
      );
    } catch {}
    return err;
  };

  // 执行一次请求（带401处理与刷新重试）
  const executeRequest = async () => {
    try {
      const response = await fetch(url, config);
      const data = await response.json();

      // 检查 422 验证错误（输入参数错误）
      if (response.status === 422) {
        // Pydantic 验证错误格式: { detail: [{loc, msg, type}] }
        const errorMsg = data.detail
          ? data.detail.map(err => err.msg).join('; ')
          : data.msg || '输入数据格式错误';

        logError(new Error(`验证错误: ${errorMsg}`), { endpoint, status: 422, detail: data.detail });
        throw new Error(errorMsg);
      }

      // 检查 401 错误（token 过期）
      if (response.status === 401) {
        // 如果是刷新token的请求，直接返回错误
        if (endpoint === '/api/v1/user/refresh-token') {
          throw new Error(data.msg || 'Refresh token 失败');
        }

        // 记录错误日志
        logError(new Error('Token过期'), { endpoint, status: 401 });

        // 尝试刷新 token（并发时复用同一个刷新 promise）
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            // 在所有等待方拿到结果后清空
            setTimeout(() => { refreshPromise = null; }, 0);
          });
        }
        const refreshSuccess = await refreshPromise;
        if (!refreshSuccess) {
          // 刷新失败，清除所有 token 并抛出错误
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('access_token_expire');
          localStorage.removeItem('refresh_token_expire');

          // 如果是在页面上，可以触发重新登录
          window.dispatchEvent(new CustomEvent('auth:logout'));

          throw new Error('认证已过期，请重新登录');
        }

        // 使用新 token 重试请求
        const newToken = localStorage.getItem('access_token');
        config.headers['Authorization'] = `Bearer ${newToken}`;

        const retryResponse = await fetch(url, config);
        const retryData = await retryResponse.json();

        const retryMembershipError = handleMembershipError(retryResponse, retryData);
        if (retryMembershipError) {
          throw retryMembershipError;
        }

        // 检查重试后的 422 错误
        if (retryResponse.status === 422) {
          const errorMsg = retryData.detail
            ? retryData.detail.map(err => err.msg).join('; ')
            : retryData.msg || '输入数据格式错误';
          throw new Error(errorMsg);
        }

        // 根据文档，错误码不为 0 时表示错误
        if (retryData.code !== 0 && retryData.code !== 1) {
          throw new Error(retryData.msg || '请求失败');
        }

        return retryData;
      }

      const membershipError = handleMembershipError(response, data);
      if (membershipError) {
        throw membershipError;
      }

      // 根据文档，错误码不为 0 时表示错误
      if (data.code !== 0 && data.code !== 1) {
        throw new Error(data.msg || '请求失败');
      }

      return data;
    } catch (error) {
      // 记录错误日志
      logError(error, { endpoint, options });

      // 重新抛出错误，让调用者处理
      throw error;
    }
  };

  // 直接执行请求（不全局排队，避免返回 undefined）
  return executeRequest();
}

/**
 * 带错误处理的 API 请求包装器
 */
export const apiRequestWithErrorHandling = withErrorHandling(apiRequest);

/**
 * 获取 API 版本信息
 * @returns {Promise<object>} 版本信息
 */
export async function getApiVersion() {
  return apiRequest('/api/v1/system/version');
}

export async function getCurrentUserPreferences() {
  return apiRequest('/api/v1/user/me');
}

export async function updateCurrentUserLang(lang) {
  return apiRequest('/api/v1/user/me/lang', {
    method: 'PATCH',
    body: JSON.stringify({ lang }),
  });
}

/**
 * 获取交易员排行榜
 */
export async function getTraderLeaderboard({
  page = 1,
  pageSize = 20,
  sortBy = 'pnl',
  sortOrder = 'desc',
  domains = [],
  search = '',
  tag = LEADERBOARD_TAG,
} = {}) {
  const params = new URLSearchParams();
  params.set('page', page);
  params.set('page_size', pageSize);
  params.set('sort_by', sortBy);
  params.set('sort_order', sortOrder);
  if (tag) {
    params.set('tag', tag);
  }
  if (Array.isArray(domains)) {
    domains.filter(Boolean).forEach((domain) => params.append('domains', domain));
  }
  const trimmed = search.trim();
  if (trimmed) {
    params.set('search', trimmed);
  }
  return apiRequest(`/api/v1/traders?${params.toString()}`);
}

/**
 * 搜索交易员（地址或用户名）
 */
export async function searchTraders(query, limit = 6) {
  const trimmed = (query || '').trim();
  if (!trimmed) return { data: { items: [] } };
  const params = new URLSearchParams();
  params.set('query', trimmed);
  params.set('limit', limit);
  return apiRequest(`/api/v1/traders/search?${params.toString()}`);
}

/**
 * 获取交易员详情
 */
export async function getTraderDetail(
  address,
  { historyPage = 1, historyPageSize = 100, tag } = {}
) {
  if (!address) throw new Error('address required');
  const params = new URLSearchParams();
  params.set('history_page', historyPage);
  params.set('history_page_size', historyPageSize);
  if (tag) params.set('tag', tag);
  return apiRequest(`/api/v1/traders/${address}?${params.toString()}`);
}

/**
 * 获取交易员绩效走势数据
 * @param {string} address - 交易员地址
 * @param {string} period - 聚合维度：day（按天）/week（按周）/month（按月）
 * @returns {Promise<object>} { code, msg, data: { address, period, series, summary } }
 */
export async function getTraderPerformance(address, period = 'day', tag) {
  if (!address) throw new Error('address required');
  const params = new URLSearchParams();
  params.set('period', period);
  if (tag) params.set('tag', tag);
  return apiRequest(`/api/v1/traders/${address}/performance?${params.toString()}`);
}

/**
 * 获取交易员当前持仓（按需加载）
 */
export async function getTraderActivePositions(address, tag) {
  if (!address) throw new Error('address required');
  const params = new URLSearchParams();
  if (tag) params.set('tag', tag);
  return apiRequest(`/api/v1/traders/${address}/positions?${params.toString()}`);
}

/**
 * 获取交易员活动记录（按需加载）
 */
export async function getTraderActivity(
  address,
  { page = 1, pageSize = 100, tag } = {}
) {
  if (!address) throw new Error('address required');
  const params = new URLSearchParams();
  params.set('page', page);
  params.set('page_size', pageSize);
  if (tag) params.set('tag', tag);
  return apiRequest(`/api/v1/traders/${address}/activity?${params.toString()}`);
}

/**
 * 获取交易员 AI 洞察分析
 * @param {string} address - 交易员地址
 * @param {string} lang - 语言代码，默认 'zh'
 * @returns {Promise<object>} { code, msg, data }
 */
export async function getTraderInsight(address, lang = 'zh') {
  if (!address) throw new Error('address required');
  const params = new URLSearchParams();
  params.set('lang', lang);
  return apiRequest(`/api/v1/traders/${address}/analysis?${params.toString()}`);
}

/**
 * 获取邮箱绑定图片验证码
 */
export async function getEmailCaptcha() {
  return apiRequest('/api/v1/user/email/captcha');
}

/**
 * 获取邮箱登录图片验证码
 */
export async function getEmailLoginCaptcha() {
  return apiRequest('/api/v1/user/email/login-captcha');
}

/**
 * 发送邮箱绑定验证码（需图片验证码）
 */
export async function requestEmailBindCode({ email, captchaId, captchaCode, lang = 'zh' }) {
  return apiRequest('/api/v1/user/email/request-bind-code', {
    method: 'POST',
    body: JSON.stringify({
      email,
      captchaId,
      captchaCode,
      lang
    })
  });
}

/**
 * 绑定邮箱
 */
export async function bindEmail({ email, code, lang = 'zh' }) {
  return apiRequest('/api/v1/user/email/bind', {
    method: 'POST',
    body: JSON.stringify({
      email,
      code,
      lang
    })
  });
}

/**
 * 发送邮箱登录验证码（需图片验证码）
 */
export async function requestEmailLoginCode({ email, captchaId, captchaCode, lang = 'zh' }) {
  return apiRequest('/api/v1/user/email/request-login-code', {
    method: 'POST',
    body: JSON.stringify({
      email,
      captchaId,
      captchaCode,
      lang
    })
  });
}

/**
 * 邮箱验证码登录
 */
export async function emailLogin({ email, code, lang = 'zh' }) {
  return apiRequest('/api/v1/user/email/login', {
    method: 'POST',
    body: JSON.stringify({
      email,
      code,
      lang
    })
  });
}

/**
 * 设置用户自定义名称
 * @param {object} params
 * @param {string} params.targetType - 目标类型，例如 trader/copy_config
 * @param {string} params.targetId - 目标唯一标识
 * @param {string} params.customName - 自定义名称（空字符串表示清除）
 */
export async function setCustomName({ targetType, targetId, customName }) {
  return apiRequest('/api/v1/custom-names', {
    method: 'POST',
    body: JSON.stringify({
      target_type: targetType,
      target_id: targetId,
      custom_name: customName
    })
  });
}

/**
 * Telegram 登录
 * @param {object} params - 登录参数
 * @param {string} params.user_id - 用户 ID
 * @param {string} params.code - 验证码
 * @param {string} params.id - 票据 ID
 * @returns {Promise<object>} 登录响应，包含 access_token 和 refresh_token
 */
export async function telegramLogin(params) {
  const { user_id, code, id } = params;
  return apiRequest(`/api/v1/user/tglogin?user_id=${user_id}&code=${code}&id=${id}`, {
    method: 'GET',
  });
}

/**
 * 获取用户关注的标签列表
 * @param {string} userId - 用户 ID
 * @returns {Promise<object>} 标签列表
 */
export async function getUserCategories(userId) {
  return apiRequest(`/api/v1/user/${userId}/categories`);
}

/**
 * 更新用户关注的标签列表
 * @param {string} userId - 用户 ID
 * @param {Array<string>} categories - 标签 slug 列表
 * @returns {Promise<object>} 更新结果
 */
export async function updateUserCategories(userId, categories) {
  return apiRequest(`/api/v1/user/${userId}/categories`, {
    method: 'PUT',
    body: JSON.stringify({ categories }),
  });
}

/**
 * 获取随机标签列表
 * @param {number} limit - 返回数量，默认 20
 * @returns {Promise<object>} 标签列表
 */
export async function getRandomCategories(limit = 16) {
  return apiRequest(`/api/v1/categories?limit=${limit}`);
}

/**
 * 添加或取消收藏
 * @param {string} userId
 * @param {string} marketSlug
 * @param {'add'|'delete'} action
 */
export async function updateFavorite(userId, marketSlug, action = 'add') {
  return apiRequest(`/api/v1/user/${userId}/favorites`, {
    method: 'PUT',
    body: JSON.stringify({ user_id: userId, market_slug: marketSlug, action }),
  });
}

/**
 * 获取用户收藏列表
 * @param {string} userId
 */
export async function getFavorites(userId) {
  return apiRequest(`/api/v1/user/${userId}/favorites`);
}

/**
 * 获取推荐市场列表（分页）
 * @param {number} page - 页码（从1开始）
 * @param {number} pageSize - 每页数量，默认5
 * @returns {Promise<object>} { code, msg, data: { items, pagination } }
 */
export async function getRecommendedMarkets(page = 1, pageSize = 5) {
  return apiRequest(`/api/v1/markets?page=${page}&page_size=${pageSize}`);
}

/**
 * 搜索市场
 * @param {string} keyword - 搜索关键词
 * @param {number} limit - 返回结果数量，默认 8
 * @returns {Promise<object>} { code, msg, data: { items } }
 */
export async function searchMarkets(keyword, limit = 8) {
  const query = (keyword ?? '').trim();
  if (!query) {
    return { code: 0, msg: 'success', data: { items: [] } };
  }
  return apiRequest(`/api/v1/markets/search?query=${encodeURIComponent(query)}&limit=${limit}`);
}

/**
 * 批量获取市场上下文
 * @param {string[]} slugs - 市场 slug 列表
 * @returns {Promise<object>} { code, msg, data: [{slug, context}] }
 */
export async function getMarketContexts(slugs = []) {
  if (!Array.isArray(slugs) || slugs.length === 0) {
    return { code: 1, msg: 'success', data: [] };
  }
  const params = slugs.map((s) => `slug=${encodeURIComponent(s)}`).join('&');
  return apiRequest(`/api/v1/market/context?${params}`);
}

/**
 * 获取市场 AI 洞察分析
 * @param {string} slug - 市场 slug
 * @param {string} lang - 语言代码，默认 'zh'
 * @returns {Promise<object>} { code, msg, data: { question, analysis, recommendation } }
 */
export async function getMarketInsight(slug, lang = 'zh') {
  if (!slug) {
    throw new Error('Market slug is required');
  }
  return apiRequest(`/api/v1/market/analysis?slug=${encodeURIComponent(slug)}&lang=${lang}`);
}

/**
 * 根据 slug 获取单个市场详情
 * @param {string} slug - 市场 slug
 * @returns {Promise<object>} 市场详情数据
 */
export async function getMarketBySlug(slug) {
  if (!slug) {
    throw new Error('Market slug is required');
  }
  return apiRequest(`/api/v1/market/${encodeURIComponent(slug)}`);
}

/**
 * 获取用户钱包列表
 * @param {string} userId - 用户 ID
 * @returns {Promise<object>} 钱包列表 { code, msg, data: [WalletDetailSchema] }
 */
export async function getUserWallets(userId) {
  return apiRequest(`/api/v1/user/${userId}/wallets`);
}

/**
 * 设置交易钱包
 * @param {string} userId - 用户 ID
 * @param {string} walletAddress - 钱包地址
 * @returns {Promise<object>} 更新结果
 */
export async function setTradingWallet(userId, walletAddress) {
  return apiRequest(`/api/v1/user/${userId}/wallets/${walletAddress}/trading`, {
    method: 'PUT',
  });
}

/**
 * 导出系统钱包私钥
 * @param {string} userId
 * @param {string} walletAddress
 */
export async function exportPrivateKey(userId, walletAddress) {
  return apiRequest(`/api/v1/user/${userId}/wallets/${walletAddress}/export`);
}

/**
 * 删除/停用钱包
 * @param {string} userId
 * @param {string} walletAddress
 */
export async function deleteWallet(userId, walletAddress) {
  return apiRequest(`/api/v1/user/${userId}/wallets/${walletAddress}`, {
    method: 'DELETE',
  });
}

/**
 * 获取用户白名单
 * @param {string} userId
 */
export async function getWhitelist(userId) {
  return apiRequest(`/api/v1/user/${userId}/whitelist`);
}

/**
 * 添加白名单地址
 * @param {string} userId
 * @param {string} address
 */
export async function addWhitelist(userId, address) {
  const formData = new URLSearchParams();
  formData.append('address', address);
  return apiRequest(`/api/v1/user/${userId}/whitelist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData.toString(),
  });
}

/**
 * 移除白名单地址
 * @param {string} userId
 * @param {string} address
 */
export async function removeWhitelist(userId, address) {
  const formData = new URLSearchParams();
  formData.append('address', address);
  return apiRequest(`/api/v1/user/${userId}/whitelist`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData.toString(),
  });
}

/**
 * 获取单个钱包的实时余额
 * @param {string} userId - 用户 ID
 * @param {string} walletAddress - 钱包地址（Safe 优先传 Safe 地址）
 * @returns {Promise<object>} { code, msg, data: { usdc, pol, usdc_native } }
 */
export async function getWalletBalance(userId, walletAddress) {
  if (!userId) throw new Error('userId is required');
  if (!walletAddress) throw new Error('walletAddress is required');
  const addr = encodeURIComponent(walletAddress);
  return apiRequest(`/api/v1/user/${userId}/wallets/${addr}/balance`);
}

/**
 * 查询转账任务状态
 * @param {string} userId
 * @param {string} jobId
 */
export async function getTransferJobStatus(userId, jobId) {
  return apiRequest(`/api/v1/user/${userId}/transfer-jobs/${jobId}`);
}

/**
 * 获取用户持仓列表
 * @param {string} userId - 用户 ID
 * @returns {Promise<object>} 持仓数据 { code, msg, data: { wallets, summary } }
 */
export async function getUserPositions(userId) {
  return apiRequest(`/api/v1/user/${userId}/positions`);
}

/**
 * 获取用户历史持仓（已结算/已赎回）
 * @param {string} userId - 用户 ID
 * @param {number | null} limit - 返回数量限制，传 null 表示全量
 * @returns {Promise<object>} 历史持仓数据 { code, msg, data: { wallets, stats } }
 */
export async function getUserHistoricalPositions(userId, limit = null) {
  const query =
    typeof limit === 'number' && Number.isFinite(limit) ? `?limit=${limit}` : '';
  return apiRequest(`/api/v1/user/${userId}/positions/history${query}`);
}

/**
 * 获取用户持仓统计（胜率、盈亏等）
 * @param {string} userId - 用户 ID
 * @returns {Promise<object>} 统计数据 { code, msg, data: { stats, wallets_stats } }
 */
export async function getUserPositionStats(userId) {
  return apiRequest(`/api/v1/user/${userId}/positions/stats`);
}

/**
 * 创建持仓卖出深链
 * @param {string} userId - 用户 ID
 * @param {object} payload - { condition_id, outcome, wallet_address?, market_title?, outcome_emoji? }
 * @returns {Promise<object>} { code, msg, data: { deep_link, intent_id, expires_in } }
 */
export async function createPositionSellLink(userId, payload) {
  return apiRequest(`/api/v1/user/${userId}/positions/sell-link`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * 直接卖出持仓（Web 端确认后执行）
 * @param {string} userId - 用户 ID
 * @param {object} payload - { condition_id, outcome, wallet_address?, order_type? }
 * @returns {Promise<object>} { code, msg, data: { success, filled_size, avg_price, total_cost, error? } }
 */
export async function sellUserPosition(userId, payload) {
  return apiRequest(`/api/v1/user/${userId}/positions/sell`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * 创建临时组合跟单���置
 * @param {object} params - { trader_addresses: string[], sort_by?: string }
 * @returns {Promise<object>} { code, msg, data: { config_id, deep_link, trader_count } }
 */
export async function createTempPortfolioConfig(params) {
  return apiRequest('/api/v1/temp-configs', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * 创建跟单配置（Web 端）
 * @param {object} payload - { config_name?, trader_addresses, amount_per_market, investment_total_limit?, stop_loss_enabled?, stop_loss_limit?, copy_sell_enabled?, auto_copy_enabled?, market_cooldown_enabled?, market_cooldown_minutes? }
 * @returns {Promise<object>} { code, msg, data: { id, config_name, config_type, trader_addresses, auto_copy_enabled, created_at } }
 */
export async function createCopyConfig(payload) {
  return apiRequest('/api/v1/copy-configs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * 获取用户跟单配置列表
 * @param {string} userId - 用户ID
 * @returns {Promise<object>} { code, msg, data: { configs, stats } }
 */
export async function getUserCopyConfigs(userId) {
  return apiRequest(`/api/v1/user/${userId}/copy-configs`);
}

/**
 * 获取单个跟单配置详情（包含持仓数据）
 * @param {string} configId - 配置ID
 * @returns {Promise<object>} { code, msg, data }
 */
export async function getCopyConfigDetail(configId) {
  return apiRequest(`/api/v1/copy-configs/${configId}`);
}

/**
 * 获取跟单配置的进行中持仓（分页）
 * @param {string} configId - 配置ID
 * @param {number} page - 页码（从1开始）
 * @param {number} pageSize - 每页数量
 * @returns {Promise<object>} { code, msg, data: { items, total, page, page_size, total_pages } }
 */
export async function getCopyConfigPositions(configId, page = 1, pageSize = 20) {
  return apiRequest(`/api/v1/copy-configs/${configId}/positions?page=${page}&page_size=${pageSize}`);
}

/**
 * 获取跟单配置的已结算持仓（分页）
 * @param {string} configId - 配置ID
 * @param {number} page - 页码（从1开始）
 * @param {number} pageSize - 每页数量
 * @returns {Promise<object>} { code, msg, data: { items, total, page, page_size, total_pages } }
 */
export async function getCopyConfigClosedPositions(configId, page = 1, pageSize = 20) {
  return apiRequest(`/api/v1/copy-configs/${configId}/closed-positions?page=${page}&page_size=${pageSize}`);
}

/**
 * 获取跟单配置的交易流水（分页）
 * @param {string} userId - 用户ID
 * @param {string} configId - 配置ID
 * @param {number} page - 页码（从1开始）
 * @param {number} pageSize - 每页数量
 * @returns {Promise<object>} { code, msg, data: { items, total, page, total_pages } }
 */
export async function getCopyConfigTrades(userId, configId, page = 1, pageSize = 10) {
  return apiRequest(`/api/v1/user/${userId}/copy-configs/${configId}/trades?page=${page}&page_size=${pageSize}`);
}

/**
 * 更新账号级全局跟单风控（亏损上限）
 * @param {string} userId - 用户ID
 * @param {number|null} copyGlobalLossLimit - 亏损上限(USDC)，null 表示无上限
 */
export async function updateCopyGlobalRisk(userId, copyGlobalLossLimit) {
  return apiRequest(`/api/v1/user/${userId}/copy-global-risk`, {
    method: 'PUT',
    body: JSON.stringify({ copy_global_loss_limit: copyGlobalLossLimit }),
  });
}

/**
 * 暂停账号级全局跟单
 * @param {string} userId - 用户ID
 */
export async function pauseCopyGlobalRisk(userId) {
  return apiRequest(`/api/v1/user/${userId}/copy-global-risk/pause`, {
    method: 'POST',
  });
}

/**
 * 解除账号级全局暂停并恢复自动跟单
 * @param {string} userId - 用户ID
 */
export async function resumeCopyGlobalRisk(userId) {
  return apiRequest(`/api/v1/user/${userId}/copy-global-risk/resume`, {
    method: 'POST',
  });
}

/**
 * 更新策略级风控配置（投资上限/止损/同市场同方向冷却）
 * @param {string} configId - 配置ID
 * @param {object} payload - { investment_total_limit?: number|null, stop_loss_enabled?: boolean, stop_loss_limit?: number|null, market_cooldown_enabled?: boolean, market_cooldown_minutes?: number|null }
 */
export async function updateCopyConfigRisk(configId, payload) {
  return apiRequest(`/api/v1/copy-configs/${configId}/risk`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

/**
 * 更新配置状态(启用/停用)
 * @param {string} configId - 配置ID
 * @param {boolean} isActive - 是否启用
 * @returns {Promise<object>} { code, msg, data }
 */
export async function updateConfigStatus(configId, isActive) {
  return apiRequest(`/api/v1/copy-configs/${configId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ is_active: isActive }),
  });
}

/**
 * 更新配置自动跟单开关
 * @param {string} configId - 配置ID
 * @param {boolean} enabled - 是否开启自动跟单
 * @returns {Promise<object>} { code, msg, data }
 */
export async function updateConfigAutoCopy(configId, enabled) {
  return apiRequest(`/api/v1/copy-configs/${configId}/auto-copy`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

/**
 * 切换配置自动跟单开关(已废弃,使用updateConfigAutoCopy)
 * @param {string} configId - 配置ID
 * @returns {Promise<object>} { code, msg, data: { auto_copy_enabled } }
 */
export async function toggleConfigAutoCopy(configId) {
  return apiRequest(`/api/v1/copy-configs/${configId}/toggle-auto`, {
    method: 'POST',
  });
}

/**
 * 删除跟单配置
 * @param {string} configId - 配置ID
 * @returns {Promise<object>} { code, msg }
 */
export async function deleteConfig(configId) {
  return apiRequest(`/api/v1/copy-configs/${configId}`, {
    method: 'DELETE',
  });
}

/**
 * 删除跟单配置(别名,已废弃)
 * @param {string} configId - 配置ID
 * @returns {Promise<object>} { code, msg }
 */
export async function deleteCopyConfig(configId) {
  return deleteConfig(configId);
}

/**
 * 获取用户邀请统计
 * @param {string} userId - 用户 ID
 * @returns {Promise<object>} { code, msg, data: { has_invite_code, invite_code, can_invite, total_invitations, invitations } }
 */
export async function getUserInviteStats(userId) {
  return apiRequest(`/api/v1/user/${userId}/invite-stats`);
}

/**
 * 创建分享链接（需要登录）
 * @param {object} params - 分享链接参数
 * @param {string} params.share_type - 分享类型：leaderboard/trader/market/portfolio
 * @param {string} params.target_page - 目标页面路径，如：/leaderboard
 * @param {object} params.target_params - 目标页面参数（JSON格式）
 * @param {object} [params.metadata] - 额外元数据（可选）
 * @returns {Promise<object>} { code, msg, data: { share_id, share_url, expires_at } }
 * @note user_id 会从后端 JWT token 自动解析，无需前端传递
 */
export async function createShareLink(params) {
  return apiRequest('/api/v1/share', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * 获取分享链接统计
 * @param {string} shareId - 分享链接 ID
 * @returns {Promise<object>} { code, msg, data: { share_id, share_type, total_clicks, total_conversions, ... } }
 */
export async function getShareStats(shareId) {
  return apiRequest(`/api/v1/share/${shareId}/stats`);
}

/**
 * 记录分享链接转化事件
 * @param {string} shareId - 分享链接 ID
 * @returns {Promise<object>} { code, msg, data: { share_id, recorded } }
 */
export async function recordShareConversion(shareId) {
  return apiRequest(`/api/v1/share/${shareId}/conversion`, {
    method: 'POST',
  });
}

/**
 * 应用邀请码（需要登录）
 * @param {string} inviteCode - 6位邀请码
 * @returns {Promise<object>} { code, msg, data: { success, taint_cleared, message } }
 */
export async function applyInviteCode(inviteCode) {
  return apiRequest('/api/v1/invite/apply', {
    method: 'POST',
    body: JSON.stringify({ invite_code: inviteCode }),
  });
}

/**
 * 获取当前用户会员信息
 */
export async function getMembershipMe() {
  return apiRequest('/api/v1/membership/me');
}

/**
 * 获取所有会员方案
 */
export async function getMembershipTiers() {
  return apiRequest('/api/v1/membership/tiers');
}

/**
 * 获取点数余额（需要登录）
 * @returns {Promise<object>} { code, msg, data: { balance, total_earned, total_spent } }
 */
export async function getPointsBalance() {
  return apiRequest('/api/v1/points/balance');
}

/**
 * 创建会员支付订单
 */
export async function createPaymentCheckout(payload) {
  return apiRequest('/api/v1/payment/create-checkout', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * 创建点数购买订单（需要登录）
 * @param {object} payload - { package_code, chain?, token?, pay_currency? }
 */
export async function createPointsCheckout(payload) {
  return apiRequest('/api/v1/payment/create-points-checkout', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * 获取支付订单信息
 */
export async function getPaymentOrder(orderId, { refresh = false } = {}) {
  const params = new URLSearchParams();
  if (refresh) params.set('refresh', 'true');
  const query = params.toString();
  return apiRequest(`/api/v1/payment/orders/${orderId}${query ? `?${query}` : ''}`);
}

/**
 * 获取支付历史记录
 */
export async function getPaymentHistory() {
  return apiRequest('/api/v1/payment/history');
}

/**
 * 获取支付网络/币种选项
 */
export async function getNowpaymentsOptions() {
  return apiRequest('/api/v1/payment/nowpayments/options');
}

// ==================== 兑换码相关 ====================

/**
 * 验证兑换码（不执行兑换）
 * @param {string} code - 兑换码
 */
export async function validateRedemptionCode(code) {
  return apiRequest('/api/v1/redemption/validate', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/**
 * 使用兑换码兑换会员
 * @param {string} code - 兑换码
 */
export async function redeemCode(code) {
  return apiRequest('/api/v1/redemption/redeem', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}
